import React, { useContext, useState, useEffect } from 'react';
import { Paper, TextField, Button, Select, MenuItem, Grid, Typography } from '@material-ui/core';
import { DataTable, Notifications } from '../../../../shared';
import { UserContext } from '../../../../contexts/UserContext';
import { fbmApi as api } from '../../../../api/fbm';
import { warehouseApi } from '../../../../api/warehouse';
import dayjs from '~/utils/dayjsConfig';

const ViewDailySkuPack = () => {
    let [rows, setRows] = useState([]);
    let [cols, setCols] = useState([]);
    let [notification, setNotification] = useState({ text: null, level: null });
    let [reportButtonText, setReportButtonText] = useState('Run Report');
    let [facilityID, setFacilityID] = useState(''); // Initialize to empty string for no selection
    let [facilityOptions, setFacilityOptions] = useState([]);
    const user = useContext(UserContext);

    let { fbmAccount } = useContext(UserContext);

    const [reportStartDate, setReportStartDate] = React.useState(dayjs().format('YYYY-MM-DD'));
    let handleReportStartDateChange = (event) => {
        setReportStartDate(event.target.value);
    };

    const [reportEndDate, setReportEndDate] = React.useState(dayjs().format('YYYY-MM-DD'));
    let handleReportEndDateChange = (event) => {
        setReportEndDate(event.target.value);
    };

    useEffect(() => {
        const fetchFacilities = async () => {
            if (fbmAccount && fbmAccount.facilityIDs && fbmAccount.facilityIDs.length > 0) {
                try {
                    const res = await warehouseApi.getFacilities(true); // Fetch active facilities
                    if (res.status === true) {
                        let whitelisted = res.data.rows.filter((fac) => fbmAccount.facilityIDs.includes(fac.id));
                        const options = [
                            { text: 'All Facilities', value: 0 },
                            ...whitelisted.map((fac) => ({ text: fac.name, value: fac.id })),
                        ];
                        setFacilityOptions(options);
                        // Set default facility if not already selected and options are available
                        // if (facilityID === '' && options.length > 1) {
                        //    setFacilityID(options[1].value); // Or options[0].value for 'All Facilities' by default
                        // }
                    } else {
                        setNotification({ text: 'Could not load facilities: ' + res.msg, level: 'error' });
                        setFacilityOptions([{ text: 'All Facilities', value: 0 }]);
                    }
                } catch (error) {
                    setNotification({ text: 'Error fetching facilities: ' + error.message, level: 'error' });
                    setFacilityOptions([{ text: 'All Facilities', value: 0 }]);
                }
            } else if (fbmAccount) {
                setFacilityOptions([{ text: 'All Facilities', value: 0 }]);
            }
        };

        fetchFacilities();
    }, [fbmAccount]);

    let handleRunReportClick = (event) => {
        getAndRenderReportData();
    };

    let getAndRenderReportData = async () => {
        if (facilityID === '') {
            setNotification({ text: 'Please select a facility option.', level: 'warning' });
            return;
        }
        setReportButtonText('Loading...');
        setRows([]);
        setCols([]);
        setNotification({ text: null, level: null });

        let response = await api.getReportDailySkuPackView(
            fbmAccount.fbmAccountName,
            reportStartDate,
            reportEndDate,
            facilityID, // Use selected facilityID (0 for all)
        );

        if (response.status === false) {
            setNotification({ text: 'No entries found ' + response.msg, level: 'error' });
            setRows([]);
            setCols([]);
        } else {
            let columns = [
                { accessor: 'externalSku', Header: 'External Sku' },
                { accessor: 'itemName', Header: 'Item Name' },
            ];

            if (response.data.rows && response.data.rows.cols) {
                for (let i = 0; i < response.data.rows.cols.length; i++) {
                    columns.push({ accessor: response.data.rows.cols[i], Header: response.data.rows.cols[i] });
                }
                setCols(columns);
                setRows(response.data.rows.rows);
                if (response.data.rows.rows.length === 0) {
                    setNotification({ text: 'No data found for the selected criteria.', level: 'info' });
                }
            } else {
                setNotification({ text: 'Data format error in response.', level: 'error' });
                setRows([]);
                setCols([]);
            }
        }
        setReportButtonText('Run Report');
    };

    // Removed useEffect that runs report on fbmAccount load

    return (
        <Paper style={{ padding: '16px' }}>
            <Notifications options={notification} />

            <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12}>
                    <Typography variant="h6">
                        {fbmAccount.companyName} - Units Packed per Day (Note: Can only go back 90 days)
                    </Typography>
                </Grid>

                <Grid item>
                    <TextField
                        id="reportStartDate"
                        label="Start Date"
                        type="date"
                        value={reportStartDate} // Controlled component
                        InputLabelProps={{
                            shrink: true,
                        }}
                        disabled={reportButtonText === 'Loading...'}
                        onChange={handleReportStartDateChange}
                    />
                </Grid>

                <Grid item>
                    <TextField
                        id="reportEndDate"
                        label="End Date"
                        type="date"
                        value={reportEndDate} // Controlled component
                        InputLabelProps={{
                            shrink: true,
                        }}
                        disabled={reportButtonText === 'Loading...'}
                        onChange={handleReportEndDateChange}
                    />
                </Grid>

                <Grid item>
                    <Select
                        value={facilityID}
                        onChange={(event) => {
                            setFacilityID(event.target.value);
                            setRows([]);
                            setCols([]);
                            setNotification({ text: null, level: null });
                        }}
                        displayEmpty
                        variant="outlined"
                        style={{ minWidth: 200, marginRight: '16px' }}
                    >
                        <MenuItem value="" disabled>
                            Select Facility
                        </MenuItem>
                        {facilityOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.text}
                            </MenuItem>
                        ))}
                    </Select>
                </Grid>

                <Grid item>
                    <Button
                        id="runReport"
                        onClick={handleRunReportClick}
                        color="secondary"
                        variant="contained"
                        disabled={facilityID === '' || reportButtonText === 'Loading...'}
                    >
                        {reportButtonText}
                    </Button>
                </Grid>
            </Grid>

            {rows.length === 0 && reportButtonText !== 'Loading...' && facilityID === '' && (
                <Typography variant="body1" style={{ marginTop: '16px' }}>
                    Please select a facility option and click 'Run Report'.
                </Typography>
            )}
            {rows.length === 0 && reportButtonText !== 'Loading...' && facilityID !== '' && !notification.text && (
                <Typography variant="body1" style={{ marginTop: '16px' }}>
                    Click 'Run Report' to fetch data for the selected facility and date range.
                </Typography>
            )}

            <Typography variant="caption" display="block" style={{ marginTop: '16px' }}>
                Note: Report may take up to 2 minutes to run.
            </Typography>

            <br />

            <DataTable
                csvExport={rows.length > 0 && reportButtonText === 'Run Report'}
                csvExportFileName="daily-sku-pack"
                csvExportHeadersUseAccessor={true}
                columns={cols}
                data={rows}
                editable={false}
            />
        </Paper>
    );
};

export { ViewDailySkuPack };
